import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, Bike } from "lucide-react";
import { Button } from "@/components/ui/button";

const MODES = [
  {
    key: "everyday",
    label: "Monthly · Everyday",
    sub: "Daily exterior, weekly deep clean",
    isMonthly: true,
    cadenceLabel: "weekly",
    cars: [
      { type: "Hatchback", basic: "699", premium: "899" },
      { type: "Sedan / Compact SUV", basic: "999", premium: "1,199" },
      { type: "SUV / Luxury", basic: "1,299", premium: "1,499" },
    ],
  },
  {
    key: "alternate",
    label: "Monthly · Alternate-Day",
    sub: "Every other day exterior, fortnightly deep clean",
    isMonthly: true,
    cadenceLabel: "fortnightly",
    cars: [
      { type: "Hatchback", basic: "499", premium: "699" },
      { type: "Sedan / Compact SUV", basic: "799", premium: "999" },
      { type: "SUV / Luxury", basic: "1,099", premium: "1,299" },
    ],
  },
  {
    key: "onetime",
    label: "One-Time",
    sub: "A single, on-demand clean",
    isMonthly: false,
    cadenceLabel: null,
    cars: [
      { type: "Hatchback", basic: "199", premium: "299" },
      { type: "Sedan / Compact SUV", basic: "249", premium: "349" },
      { type: "SUV / Luxury", basic: "299", premium: "399" },
    ],
  },
];

const BASIC_SERVICES = [
  { item: "Dry dusting", schedule: "every visit" },
  { item: "Pressure or bucket wash", schedule: "every visit" },
  { item: "Microfibre wipe", schedule: "every visit" },
  { item: "Tyre dressing", schedule: "every visit" },
  { item: "Exterior glass cleaning", schedule: "every visit" },
  { item: "Interior vacuuming", schedule: "cadence" },
  { item: "Boot vacuuming", schedule: "cadence" },
  { item: "Floor mat cleaning", schedule: "cadence" },
  { item: "Dashboard cleaning", schedule: "cadence" },
  { item: "Interior glass cleaning", schedule: "cadence" },
  { item: "Roof liner cleaning", schedule: "monthly", complimentary: true, monthlyOnly: true },
];

const PREMIUM_SERVICES = [
  { item: "Alloy and tyre polishing", schedule: "cadence" },
  { item: "Dashboard, console & cup polishing", schedule: "cadence" },
  { item: "Body polishing", schedule: "cadence" },
  { item: "Seat care and shampooing", schedule: "cadence" },
  { item: "Engine bay cleaning", schedule: "cadence" },
  { item: "Door panel polishing", schedule: "cadence" },
  { item: "Exterior foam wash", schedule: "cadence" },
  { item: "Roof liner, AC vent & sunroof cleaning", schedule: "monthly", complimentary: true, monthlyOnly: true },
];

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

function formatSchedule(service, mode) {
  if (service.monthlyOnly && !mode.isMonthly) return null;
  if (service.schedule === "every visit") return null;
  if (service.schedule === "monthly") {
    return service.complimentary ? "complimentary · monthly" : "monthly";
  }
  if (service.schedule === "cadence") {
    return mode.cadenceLabel;
  }
  return null;
}

function ServiceList({ services, mode, testIdPrefix }) {
  return (
    <ul className="space-y-2.5" data-testid={testIdPrefix}>
      {services.map((s) => {
        if (s.monthlyOnly && !mode.isMonthly) return null;
        const tag = formatSchedule(s, mode);
        return (
          <li key={s.item} className="flex items-start gap-3 text-sm text-zyp-navy/80">
            <Check size={14} className="text-zyp-highlight mt-1 shrink-0" strokeWidth={2.5} />
            <span className="leading-relaxed">
              {s.item}
              {tag && (
                <span className="ml-2 text-xs text-zyp-navy/50 italic">({tag})</span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function PriceCard({ car, mode, isPremiumDefault }) {
  const [tier, setTier] = useState(isPremiumDefault ? "premium" : "basic");
  const price = tier === "premium" ? car.premium : car.basic;
  return (
    <div
      data-testid={`pricing-card-${mode.key}-${car.type.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`relative rounded-2xl border transition-all duration-300 p-7 md:p-8 ${
        tier === "premium"
          ? "border-zyp-highlight/40 bg-white shadow-[0_0_40px_-20px_rgba(183,41,45,0.35)]"
          : "border-zyp-navy/10 bg-zyp-cream/20 hover:border-zyp-navy/20 hover:bg-white"
      }`}
    >
      {tier === "premium" && (
        <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zyp-highlight text-white text-[10px] font-medium tracking-wider uppercase">
          <Sparkles size={10} /> Premium
        </div>
      )}

      <div className="text-xs uppercase tracking-[0.18em] text-zyp-navy/60">
        {car.type}
      </div>

      <div className="mt-5 flex items-baseline gap-1">
        <span className="text-zyp-highlight text-lg font-medium">₹</span>
        <span className="font-heading text-5xl md:text-[56px] font-semibold tracking-tighter text-zyp-highlight">
          {price}
        </span>
        {mode.isMonthly && (
          <span className="text-zyp-navy/50 text-sm ml-1">/mo</span>
        )}
      </div>

      <div
        role="tablist"
        className="mt-6 grid grid-cols-2 gap-1 p-1 rounded-full bg-white border border-zyp-navy/10"
      >
        {["basic", "premium"].map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tier === t}
            data-testid={`tier-toggle-${mode.key}-${car.type.toLowerCase().replace(/[^a-z]+/g, "-")}-${t}`}
            onClick={() => setTier(t)}
            className={`py-2 px-3 rounded-full text-xs font-medium uppercase tracking-wide transition-all ${
              tier === t
                ? "bg-zyp-navy text-white"
                : "text-zyp-navy/60 hover:text-zyp-navy"
            }`}
          >
            {t === "basic" ? `Basic · ₹${car.basic}` : `Premium · ₹${car.premium}`}
          </button>
        ))}
      </div>

      <Button
        data-testid={`pricing-cta-${mode.key}-${car.type.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        onClick={() => scrollTo("#waitlist")}
        className={`mt-6 w-full h-11 rounded-full font-medium text-sm ${
          tier === "premium"
            ? "bg-zyp-highlight hover:bg-zyp-highlight-hover text-white"
            : "bg-zyp-navy hover:bg-zyp-navy/90 text-white"
        }`}
      >
        Reserve this plan
      </Button>
    </div>
  );
}

export default function Pricing() {
  const [activeMode, setActiveMode] = useState(MODES[0].key);
  const mode = MODES.find((m) => m.key === activeMode);

  return (
    <section
      id="plans"
      data-testid="subscription-pricing"
      className="relative py-24 md:py-32 px-6 md:px-12 border-t border-zyp-navy/10 bg-zyp-cream"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-zyp-highlight/25 bg-zyp-highlight/5 text-zyp-highlight text-xs uppercase tracking-[0.2em] mb-6">
            <Sparkles size={12} /> Limited-period launch pricing
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight max-w-3xl mx-auto text-zyp-navy">
            One subscription. <br className="sm:hidden" />
            Zero excuses.
          </h2>
          <p className="mt-5 text-zyp-navy/70 max-w-xl mx-auto">
            Pick a cadence. Pick a tier. We&rsquo;ll handle your car &mdash; and one
            bike, on the house.
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Pricing modes"
          data-testid="pricing-mode-tabs"
          className="mx-auto mb-10 max-w-2xl grid grid-cols-1 sm:grid-cols-3 gap-2 p-1.5 rounded-2xl border border-zyp-navy/10 bg-white"
        >
          {MODES.map((m) => (
            <button
              key={m.key}
              role="tab"
              aria-selected={activeMode === m.key}
              data-testid={`pricing-mode-${m.key}`}
              onClick={() => setActiveMode(m.key)}
              className={`px-3 py-3 rounded-xl text-sm transition-all ${
                activeMode === m.key
                  ? "bg-zyp-highlight text-white shadow-[0_0_30px_-10px_rgba(183,41,45,0.45)]"
                  : "text-zyp-navy/60 hover:text-zyp-navy"
              }`}
            >
              <div className="font-medium">{m.label}</div>
              <div className={`text-[11px] mt-0.5 ${activeMode === m.key ? "text-white/85" : "text-zyp-navy/45"}`}>
                {m.sub}
              </div>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={mode.key}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6"
          >
            {mode.cars.map((c, i) => (
              <PriceCard
                key={c.type}
                car={c}
                mode={mode}
                isPremiumDefault={i === 1}
              />
            ))}
          </motion.div>
        </AnimatePresence>

        {mode.isMonthly && (
          <div
            data-testid="pricing-bike-note"
            className="mt-8 max-w-2xl mx-auto rounded-2xl border border-zyp-navy/10 bg-white px-6 py-4 flex items-center gap-4"
          >
            <div className="h-10 w-10 shrink-0 rounded-lg bg-zyp-highlight/10 border border-zyp-highlight/20 flex items-center justify-center">
              <Bike size={18} className="text-zyp-highlight" />
            </div>
            <p className="text-sm text-zyp-navy/80">
              <span className="text-zyp-navy font-medium">One bike, on us.</span>{" "}
              Free with every monthly plan. Additional bikes at{" "}
              <span className="text-zyp-highlight font-semibold">₹249</span> each.
            </p>
          </div>
        )}

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div
            data-testid="pricing-services-basic"
            className="rounded-2xl border border-zyp-navy/10 bg-white p-8 md:p-10"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-zyp-navy/60 mb-2">
                  Included in
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-zyp-navy">
                  Basic
                </h3>
              </div>
              <div className="h-10 w-10 rounded-lg border border-zyp-navy/10 bg-zyp-cream flex items-center justify-center">
                <Check size={16} className="text-zyp-navy" />
              </div>
            </div>
            <ServiceList
              services={BASIC_SERVICES}
              mode={mode}
              testIdPrefix="basic-services-list"
            />
          </div>

          <div
            data-testid="pricing-services-premium"
            className="relative rounded-2xl border border-zyp-highlight/30 bg-white p-8 md:p-10 overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 h-48 w-48 rounded-full bg-zyp-highlight/10 blur-3xl pointer-events-none" />
            <div className="relative flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-zyp-highlight mb-2">
                  Everything in Basic, plus
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-zyp-navy">
                  Premium
                </h3>
              </div>
              <div className="h-10 w-10 rounded-lg bg-zyp-highlight flex items-center justify-center">
                <Sparkles size={16} className="text-white" />
              </div>
            </div>
            <div className="relative">
              <ServiceList
                services={PREMIUM_SERVICES}
                mode={mode}
                testIdPrefix="premium-services-list"
              />
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-zyp-navy/50 mt-10">
          GST included &middot; Pause or cancel anytime &middot; Photo-verified every visit
        </p>
      </div>
    </section>
  );
}
